import { Attempt } from '@/common/types/session';
import { ControllerType } from '@/common/types/student';
import {
  segmentTypedAnswer,
  validateSegmentedAnswer,
} from '@/common/utils/segmentTypedAnswer';
import {
  CorrectChoice,
  IncorrectChoice,
} from '@/dashboard/LearnerView/ActivityFeed/AttemptTables/CellStyles';
import { HandGrabbingIcon, MouseLeftClickIcon } from '@phosphor-icons/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { Keyboard } from 'lucide-react';
import React from 'react';

interface GenericAttemptTableProps {
  renderHeader: () => React.ReactNode;
  renderData: () => React.ReactNode;
  isEmpty?: boolean;
}
export default function GenericAttemptTable({
  renderHeader,
  renderData,
  isEmpty = false,
}: GenericAttemptTableProps) {
  const headerNode = renderHeader();
  const tableHeadNodes = React.Children.toArray(
    (headerNode as React.ReactElement).props.children,
  );
  const colCount = React.Children.count(tableHeadNodes);

  return (
    <Table>
      <TableHeader className="bg-white">{headerNode}</TableHeader>

      <TableBody>
        {renderData()}
        {isEmpty && (
          <TableRow className="hover:bg-white">
            <TableCell
              colSpan={colCount}
              className="text-center text-neutral-400 py-4"
            >
              No attempts recorded
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export const getHasControl = (attempts: Attempt[]) =>
  attempts.every((a) => !!a.control);
export const getControlIcon = (control: ControllerType) => {
  switch (control) {
    case ControllerType.DRAG:
      return (
        <Tooltip>
          <TooltipTrigger>
            <HandGrabbingIcon size={16} />
          </TooltipTrigger>
          <TooltipContent>Drag and Drop</TooltipContent>
        </Tooltip>
      );
    case ControllerType.CLICK:
      return (
        <Tooltip>
          <TooltipTrigger>
            <MouseLeftClickIcon size={16} />
          </TooltipTrigger>
          <TooltipContent>Click to Place</TooltipContent>
        </Tooltip>
      );
    case ControllerType.KEYBOARD:
      return (
        <Tooltip>
          <TooltipTrigger>
            <Keyboard size={16} />
          </TooltipTrigger>
          <TooltipContent>Typing</TooltipContent>
        </Tooltip>
      );
  }
};

export const renderStudentAnswer = ({
  attempt,
  getCorrectSegments,
  isCorrect = attempt.studentAnswer === attempt.correctAnswer,
}: {
  attempt: Attempt;
  getCorrectSegments: (itemId: string) => string[] | undefined;
  isCorrect?: boolean;
}) => {
  const { studentAnswer, correctAnswerId } = attempt;

  // Fallback for if we can't segment the answer
  const nonSegmentedAnswer = isCorrect ? (
    <CorrectChoice>{studentAnswer}</CorrectChoice>
  ) : (
    <IncorrectChoice>{studentAnswer}</IncorrectChoice>
  );

  // Get correct segments
  const correctSegments = correctAnswerId
    ? getCorrectSegments(correctAnswerId)
    : undefined;
  if (!correctSegments) return nonSegmentedAnswer;

  // Use segmentTypedAnswer for keyboard control,
  // attempt.cardsPlayed for click/drag (if available).
  let segments = attempt.cardsPlayed;
  if (attempt.control === ControllerType.KEYBOARD) {
    segments = segmentTypedAnswer(studentAnswer, correctSegments);
  }
  if (!segments) return nonSegmentedAnswer;

  // Validate segments
  const segmentsValid = validateSegmentedAnswer(segments, correctSegments);

  // Render validated segments
  return (
    <div className="flex items-center gap-1">
      {segments.map((segment, i) =>
        segmentsValid[i] ? (
          <CorrectChoice key={i}>{segment}</CorrectChoice>
        ) : (
          <IncorrectChoice key={i}>{segment}</IncorrectChoice>
        ),
      )}
    </div>
  );
};
